import { makeAutoObservable, runInAction } from "mobx";
import { IFollowUpQuestionAnswer } from "../../../models/ReturnReasonTypes";
import { ISaveable } from "../../../stores/types";
import { ITranslationScopeContext } from "../../../translation/translation-scope";

export interface IFollowUpQuestionAnswerChanges extends Omit<Partial<IFollowUpQuestionAnswer>, 'return_reasons_fuq_id'> {
    _client_id?: string;
    return_reasons_fuq_id?: number|string;
}
export default class FollowUpQuestionAnswerStore implements ISaveable {
    private _changes: IFollowUpQuestionAnswerChanges = {} as IFollowUpQuestionAnswerChanges;

    private _currentTranslationKeys: string[] = [];

    private _translatableFields: string[] = [
        'displayName',
    ];
    
    constructor(
        private _discardAnswer: (store: FollowUpQuestionAnswerStore) => void, 
        private _model: IFollowUpQuestionAnswer = {} as IFollowUpQuestionAnswer) 
    {
        makeAutoObservable(this);
    }
    
    public setChanges(changes: IFollowUpQuestionAnswerChanges): void {        
        this._changes = changes;
    }
    
    public get id() { return this._changes._client_id ?? this._model.id; }
    public get displayName() { return this._changes.display_name ?? this._model.display_name; }
    public get deleted(): boolean { return !!this._changes._deleted; }
    public get weight() { return this._changes.weight ?? this._model.weight; }

    public setWeight(v:number) {
        this._changes.weight = Number(v ?? 0);
    }

    public setDeleted(setDeletedTranslationKeys: ITranslationScopeContext['setDeletedKeys']) {
        setDeletedTranslationKeys(this.getTranslationKeys());
        this._changes._deleted = true;
        
        /** when newly created answer is deleted - remove it from answers list in question */
        if (!this._model.id) {
            this._discardAnswer(this);
        }
    }

    public getTranslationKeys(): string[] {  
        const keyList: string[] = [];

        // in the case, if we do not need to include keys for deleted items
        if (this.deleted) {
            return keyList;
        }

        this._translatableFields.forEach(field => {
            const key = this[field as keyof FollowUpQuestionAnswerStore];
            if (key) {
                keyList.push(key.toString());
            }
        })
        
        if (this._currentTranslationKeys.length !== keyList.length || !this._currentTranslationKeys.every(k => !!keyList.find(key => key === k))) {
            runInAction(() => {
                this._currentTranslationKeys = keyList;
            });
        } 
        
        return this._currentTranslationKeys;
    }
    
    /********* ISaveable implementation *************/ 
    hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    getUpdatedModel(): IFollowUpQuestionAnswer {
        const copyChanges = { ...this._changes };
        delete copyChanges._client_id;
        delete copyChanges.return_reasons_fuq_id;
        return { ...this._model, ...(copyChanges as IFollowUpQuestionAnswer) };
    }

    commitChanges() {
        throw new Error('answer store commitChanges not implemented, not needed, after save the store is recreated');
    }
    
    public discardChanges():void {
        this._changes = {};
        /** when newly created answer is disacrded - remove it from answers list in question */
        if (!this._model.id) {
            this._discardAnswer(this);
        }
    }

    isSaving(): boolean {
        throw new Error('FollowUpQuestionAnswerStore has no saving functionality, only modify model');
    }

    save(): Promise<void> {
        throw new Error('FollowUpQuestionAnswerStore has no saving functionality, only modify model');
    }
}

